/**
 * useContentPreview
 *
 * Composable for query-driven preview intent state.
 *
 * Preview intent is detected via `?preview=true` in the URL.
 * This flag alone does not authorize access to content; record-level
 * `preview = true` filtering still decides whether a page or post can render.
 *
 * When active, the preview token is passed through to `useDirectusProxy`
 * so the server proxy authenticates with the editor's token instead of
 * the admin static token, allowing unpublished content to be fetched.
 *
 * Usage:
 * ```typescript
 * const { isPreview, previewToken, previewVersion } = useContentPreview()
 *
 * // Use in filters
 * const filter: Record<string, unknown> = { permalink: { _eq: '/about' } }
 * filter[isPreview ? 'preview' : 'status'] = {
 *   _eq: isPreview ? true : 'published'
 * }
 *
 * // Pass token to useDirectusProxy
 * await useDirectusProxy({
 *   collection: 'pages',
 *   params: { filter },
 *   previewToken: previewToken.value || undefined,
 *   previewVersion: previewVersion.value || undefined,
 * })
 * ```
 */

/** Return type for the useContentPreview composable */
export interface ContentPreviewReturn {
  /** Whether preview intent is active via query params (`?preview=true`) */
  isPreview: boolean
  /** Preview token from Directus editor (`?token=...`), null if not set */
  previewToken: string | null
  /** Content version key from Directus (for versioned collections like pages), null if not set */
  previewVersion: string | null
}

/**
 * Composable that reads preview query params from the current route and
 * exposes them as reactive values. When `?preview=true&token=xyz` is in
 * the URL, the composable returns the token so that `useDirectusProxy`
 * can pass it to the server proxy for authenticated preview requests.
 */
export function useContentPreview(): ContentPreviewReturn {
  const route = useRoute()

  const isPreview = computed(() => {
    return String(route.query.preview) === 'true'
  })

  const previewToken = computed(() => {
    if (!isPreview.value) return null
    const token = route.query.token
    return typeof token === 'string' ? token : null
  })

  const previewVersion = computed(() => {
    if (!isPreview.value) return null
    const version = route.query.version
    return typeof version === 'string' ? version : null
  })

  return {
    isPreview: unref(isPreview),
    previewToken: unref(previewToken),
    previewVersion: unref(previewVersion),
  }
}
